import { gitAxios, gitAuthHeaders } from './client';
import type {
  GitConnection,
  GitOrg,
  GitRepo,
  GitRepoWithSelection,
  GitBranch,
  GitCommit,
  GitCommitDetail,
  GitPullRequest,
  GitPullRequestDetail,
  GitPrFile,
  PushOperationCreated,
  PushOperation,
  CreateGitRepoInput,
  CreateGitBranchInput,
  CreatePullRequestInput,
  PushToGitBranchInput,
  GitFileContent,
  GitCodeSearchResult,
  GitTreeEntry,
  StagedFile,
  CommitStagedFilesInput,
} from './types';

const BASE = '/api/internal/git';

// Backend wraps all responses in { success, data, error }
// Error strings from the service are prefixed with "[INTERNAL] <tool> error: <message>".
// We surface only the human-readable part after the first colon.
function parseGitError(raw: string): string {
  const idx = raw.indexOf(':');
  return idx !== -1 ? raw.slice(idx + 1).trim() : raw;
}

function unwrap<T>(res: { data: { success: boolean; data: T; error?: string } }): T {
  const body = res.data;
  if (!body.success) {
    throw new Error(parseGitError(body.error ?? 'Request failed'));
  }
  return body.data;
}

// ── Connection ──────────────────────────────────────────────────────────────

export async function fetchGitConnection(orgId: string): Promise<GitConnection | null> {
  const res = await gitAxios.get(`${BASE}/connection`, {
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

// Records the connection from the GitHub OAuth token the MCP gateway already holds
// for this user (connected from the Git section or the Tools page — one flow serves
// both). 401 when they have not authorized GitHub anywhere yet.
export async function connectGithub(orgId: string): Promise<GitConnection> {
  const res = await gitAxios.post(
    `${BASE}/connection`,
    { org_id: orgId },
    {
      headers: gitAuthHeaders(orgId),
    }
  );
  return unwrap(res);
}

export async function disconnectGithub(orgId: string): Promise<void> {
  await gitAxios.delete(`${BASE}/connection`, { headers: gitAuthHeaders(orgId) });
}

// ── Orgs ────────────────────────────────────────────────────────────────────

export async function fetchGitOrgs(orgId: string): Promise<GitOrg[]> {
  const res = await gitAxios.get(`${BASE}/orgs`, { headers: gitAuthHeaders(orgId) });
  return unwrap(res);
}

// ── Repos ───────────────────────────────────────────────────────────────────

export async function fetchGitRepos(orgId: string): Promise<GitRepoWithSelection[]> {
  const res = await gitAxios.get(`${BASE}/repos/selection`, {
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

export async function fetchGitSelectedRepo(orgId: string): Promise<GitRepo | null> {
  const res = await gitAxios.get(`${BASE}/selected_repo`, {
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

export async function selectGitRepo(orgId: string, repoId: string): Promise<void> {
  await gitAxios.put(
    `${BASE}/selected_repo`,
    { org_id: orgId, repo_id: repoId },
    {
      headers: gitAuthHeaders(orgId),
    }
  );
}

export async function clearGitRepoSelection(orgId: string): Promise<void> {
  await gitAxios.delete(`${BASE}/selected_repo`, { headers: gitAuthHeaders(orgId) });
}

export async function createGitRepo(orgId: string, input: CreateGitRepoInput): Promise<GitRepo> {
  const res = await gitAxios.post(
    `${BASE}/repos`,
    { org_id: orgId, ...input },
    {
      headers: gitAuthHeaders(orgId),
    }
  );
  return unwrap(res);
}

// ── Branches ────────────────────────────────────────────────────────────────

export async function fetchBranchHasSalesforce(orgId: string, repoFullName: string, branch: string): Promise<boolean> {
  const res = await gitAxios.get(`${BASE}/branches/has_salesforce`, {
    params: { repo_full_name: repoFullName, branch },
    headers: gitAuthHeaders(orgId),
  });
  return (unwrap(res) as { has_salesforce: boolean }).has_salesforce;
}

export async function fetchGitBranches(orgId: string, repoFullName: string): Promise<GitBranch[]> {
  const res = await gitAxios.get(`${BASE}/branches`, {
    params: { repo_full_name: repoFullName },
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

export async function createGitBranch(orgId: string, input: CreateGitBranchInput): Promise<GitBranch> {
  const res = await gitAxios.post(
    `${BASE}/branches`,
    { org_id: orgId, ...input },
    {
      headers: gitAuthHeaders(orgId),
    }
  );
  return unwrap(res);
}

export async function fetchDefaultGitBranch(orgId: string): Promise<GitBranch | null> {
  const res = await gitAxios.get(`${BASE}/default_branch`, {
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

export async function setDefaultGitBranch(orgId: string, gitBranchId: string | null): Promise<void> {
  await gitAxios.put(
    `${BASE}/default_branch`,
    { org_id: orgId, git_branch_id: gitBranchId },
    {
      headers: gitAuthHeaders(orgId),
    }
  );
}

// ── Commits ─────────────────────────────────────────────────────────────────

export async function fetchGitCommits(orgId: string, repoFullName: string, branch: string): Promise<GitCommit[]> {
  const res = await gitAxios.get(`${BASE}/commits`, {
    params: { repo_full_name: repoFullName, branch },
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

export async function fetchGitCommit(
  orgId: string,
  repoFullName: string,
  sha: string
): Promise<GitCommitDetail | null> {
  const res = await gitAxios.get(
    `${BASE}/commits/${encodeURIComponent(sha)}`,
    {
      params: { repo_full_name: repoFullName },
      headers: gitAuthHeaders(orgId),
    }
  );
  return unwrap(res);
}

// ── Pull Requests ────────────────────────────────────────────────────────────

export async function fetchGitPullRequests(
  orgId: string,
  repoFullName: string,
  state = 'open'
): Promise<GitPullRequest[]> {
  const res = await gitAxios.get(`${BASE}/prs`, {
    params: { repo_full_name: repoFullName, state },
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

export async function fetchGitPullRequest(
  orgId: string,
  repoFullName: string,
  number: number
): Promise<GitPullRequestDetail | null> {
  const res = await gitAxios.get(`${BASE}/prs/${number}`, {
    params: { repo_full_name: repoFullName },
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

export async function fetchGitPullRequestFiles(
  orgId: string,
  repoFullName: string,
  number: number
): Promise<GitPrFile[]> {
  const res = await gitAxios.get(`${BASE}/prs/${number}/files`, {
    params: { repo_full_name: repoFullName },
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

export async function fetchGitPullRequestCommits(
  orgId: string,
  repoFullName: string,
  number: number
): Promise<GitCommit[]> {
  const res = await gitAxios.get(`${BASE}/prs/${number}/commits`, {
    params: { repo_full_name: repoFullName },
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

export async function createPullRequest(
  orgId: string,
  input: CreatePullRequestInput
): Promise<GitPullRequestDetail> {
  const res = await gitAxios.post(
    `${BASE}/prs`,
    { org_id: orgId, ...input },
    {
      headers: gitAuthHeaders(orgId),
    }
  );
  return unwrap(res);
}

export async function mergePullRequest(
  orgId: string,
  repoFullName: string,
  number: number,
  mergeMethod = 'squash'
): Promise<void> {
  await gitAxios.post(
    `${BASE}/prs/${number}/merge`,
    { org_id: orgId, repo_full_name: repoFullName, merge_method: mergeMethod },
    {
      headers: gitAuthHeaders(orgId),
    }
  );
}

// ── Push (async) ─────────────────────────────────────────────────────────────

export async function pushToGitBranch(
  orgId: string,
  input: PushToGitBranchInput
): Promise<PushOperationCreated> {
  const res = await gitAxios.post(
    `${BASE}/push`,
    { org_id: orgId, ...input },
    {
      headers: gitAuthHeaders(orgId),
    }
  );
  return unwrap(res);
}

// ── Code Explore ─────────────────────────────────────────────────────────────

export async function fetchGitFile(
  orgId: string,
  repoFullName: string,
  path: string,
  ref?: string,
): Promise<GitFileContent | null> {
  const res = await gitAxios.get(`${BASE}/file`, {
    params: { repo_full_name: repoFullName, path, ...(ref ? { ref } : {}) },
    headers: gitAuthHeaders(orgId),
  });
  try {
    return unwrap(res);
  } catch (e: any) {
    if (e?.message?.toLowerCase().includes('not found')) return null;
    throw e;
  }
}

export async function fetchGitTree(
  orgId: string,
  repoFullName: string,
  path: string,
  ref?: string,
): Promise<GitTreeEntry[]> {
  const res = await gitAxios.get(`${BASE}/tree`, {
    params: { repo_full_name: repoFullName, path, ...(ref ? { ref } : {}) },
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

export async function searchGitCode(
  orgId: string,
  query: string,
  repoFullName?: string,
): Promise<GitCodeSearchResult[]> {
  const res = await gitAxios.get(`${BASE}/search`, {
    params: { query, ...(repoFullName ? { repo_full_name: repoFullName } : {}) },
    headers: gitAuthHeaders(orgId),
  });
  return unwrap(res);
}

export async function pushSalesforceToGit(
  orgId: string,
  repoFullName: string,
  commitMessage?: string,
  branch?: string,
): Promise<PushOperationCreated> {
  const res = await gitAxios.post(
    `${BASE}/push_salesforce`,
    {
      org_id: orgId,
      repo_full_name: repoFullName,
      ...(commitMessage ? { commit_message: commitMessage } : {}),
      ...(branch ? { branch } : {}),
    },
    { headers: gitAuthHeaders(orgId) },
  );
  return unwrap(res);
}

export async function fetchPushOperation(orgId: string, pushOperationId: string): Promise<PushOperation> {
  try {
    const res = await gitAxios.get(
      `${BASE}/push_operations/${encodeURIComponent(pushOperationId)}`,
      { headers: gitAuthHeaders(orgId) },
    );
    // success:false with data present = terminal "failed" state, not an API error
    const body = res.data as { success: boolean; data: PushOperation; error?: string };
    if (body.data) return body.data;
    return unwrap(res);
  } catch (err: any) {
    // If the server returned a non-2xx but still included the operation data
    // (e.g. 422 with status:"failed"), surface that data so the poller can stop.
    const data = err?.response?.data?.data as PushOperation | undefined;
    if (data?.status) return data;
    throw err;
  }
}

// ── Staged files ──────────────────────────────────────────────────────────────

export async function fetchStagedFiles(orgId: string): Promise<StagedFile[]> {
  const res = await gitAxios.get(`${BASE}/staged`, { headers: gitAuthHeaders(orgId) });
  return unwrap(res);
}

export async function stageFiles(
  orgId: string,
  files: Array<{ path: string; metadata_branch_id?: string }>,
): Promise<{ staged_count: number }> {
  const res = await gitAxios.post(
    `${BASE}/staged`,
    { org_id: orgId, files },
    { headers: gitAuthHeaders(orgId) },
  );
  return unwrap(res);
}

export async function unstageFiles(orgId: string, paths?: string[]): Promise<void> {
  await gitAxios.delete(`${BASE}/staged`, {
    headers: gitAuthHeaders(orgId),
    data: { org_id: orgId, ...(paths ? { paths } : {}) },
  });
}

export async function commitStagedFiles(
  orgId: string,
  input: CommitStagedFilesInput,
): Promise<PushOperationCreated> {
  const res = await gitAxios.post(
    `${BASE}/staged/commit`,
    { org_id: orgId, ...input },
    { headers: gitAuthHeaders(orgId) },
  );
  return unwrap(res);
}
